/**
 * Reloads the app when a newer build has been deployed, so nobody keeps working on a stale bundle
 * (old lazy chunks disappear from the server and screens start failing with generic errors).
 *
 * - Every few minutes, and whenever the tab becomes visible again, fetch /version.json (never cached)
 *   and compare its main bundle name with the one this page loaded. Different → reload.
 * - If a lazy chunk fails to load (ChunkLoadError), refresh that file in the browser cache and
 *   reload once (guarded with sessionStorage). The ErrorBoundary calls this too, because React
 *   swallows a failed lazy() import before it ever reaches window "error"/"unhandledrejection".
 */
const CHECK_INTERVAL_MS = 3 * 60 * 1000;
const RELOAD_GUARD_KEY = "wapp_chunk_reload_at";
const CHUNK_ERROR_RE = /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk/i;

const mensajeDe = (error: unknown): string => {
	const e: any = error;
	return String((e && (e.message || e.name)) || e || "");
};

/** True for the error webpack throws when a lazy chunk cannot be loaded (deploy in between, cache, network). */
export const esErrorDeChunk = (error: unknown): boolean => CHUNK_ERROR_RE.test(mensajeDe(error));

const currentMainBundle = (): string | null => {
	const script = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))
		.map((element) => element.src)
		.find((src) => /\/static\/js\/main\.[a-z0-9]+\.js$/i.test(src));
	if (!script) {
		return null;
	}
	return script.substring(script.lastIndexOf("/") + 1);
};

/** Did reloadOnce already fire in the last minute? Then a second reload would not help. */
export const yaSeRecargoHacePoco = (): boolean => {
	try {
		return Date.now() - Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0) < 60 * 1000;
	} catch (_) {
		return false;
	}
};

export const reloadOnce = (reason: string): boolean => {
	try {
		if (yaSeRecargoHacePoco()) {
			return false; // already reloaded very recently; avoid a loop
		}
		sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
	} catch (_) { /* storage unavailable: still reload */ }
	console.info("WAPP: recargando por " + reason);
	window.location.reload();
	return true;
};

/**
 * Recovers from a chunk that did not load. The browser may be holding a bad cached copy of that
 * URL (chunks are served as immutable), so first refresh that single entry from the network,
 * then reload the page once. Returns false when a reload already happened a moment ago.
 */
export const recargarPorChunk = (error: unknown): boolean => {
	if (yaSeRecargoHacePoco()) {
		return false;
	}
	const match = mensajeDe(error).match(/\((?:missing|error|timeout):\s*(https?:[^)\s]+)\)/i);
	const terminar = () => reloadOnce("chunk que no cargó (" + (match ? match[1] : "sin URL") + ")");
	if (!match) {
		terminar();
		return true;
	}
	let hecho = false;
	const unaVez = () => {
		if (!hecho) {
			hecho = true;
			terminar();
		}
	};
	window.setTimeout(unaVez, 1500); // never wait on a slow network
	try {
		fetch(match[1], { cache: "reload", mode: "no-cors" }).then(unaVez, unaVez);
	} catch (_) {
		unaVez();
	}
	return true;
};

// New build found while someone is working: wait until the tab is hidden or they've been idle a while,
// so a deploy never reloads WappChat/WappTeam in the middle of typing (Esteban, 2026-10-11). Old chunks stay on the server.
const IDLE_MS = 5 * 60 * 1000;
let ultimoUso = Date.now();
let pendiente: string | null = null;
// Each tab waits a random 0–10 min after noticing a new build, so a deploy doesn't reload 200+ screens at once (12-oct-2026).
const ESPERA_AZAR = Math.floor(Math.random() * 10 * 60 * 1000);
let vistoEn = 0;
// Never with a call going on (Esteban, 2026-10-14): the call lives in this tab, reloading hangs it up. A long call looks
// "idle" and people switch tabs while talking. After it ends, wait another IDLE_MS so the call notes can be filled in.
const frenos: (() => boolean)[] = [];
let frenadoEn = 0;
export const frenarRecarga = (fn: () => boolean) => { frenos.push(fn); };
const recargarSiConviene = () => {
	if (pendiente && !vistoEn) vistoEn = Date.now();
	if (frenos.some((f) => { try { return f(); } catch (_) { return false; } })) frenadoEn = Date.now();
	if (pendiente && Date.now() - vistoEn >= ESPERA_AZAR && Date.now() - frenadoEn > IDLE_MS && (document.visibilityState === "hidden" || Date.now() - ultimoUso > IDLE_MS)) {
		reloadOnce("nueva versión publicada (" + pendiente + ")");
	}
};

const checkVersion = async () => {
	const mine = currentMainBundle();
	if (!mine) {
		return;
	}
	try {
		const response = await fetch("/version.json?_=" + Date.now(), { cache: "no-store" });
		if (!response.ok) {
			return;
		}
		const data = await response.json();
		if (data && typeof data.main === "string" && data.main !== mine) {
			pendiente = data.main;
			recargarSiConviene();
		}
	} catch (_) {
		// offline or transient error: try again on the next tick
	}
};

export const startVersionWatcher = () => {
	if (process.env.NODE_ENV !== "production") {
		return;
	}
	window.setInterval(checkVersion, CHECK_INTERVAL_MS);
	window.setInterval(recargarSiConviene, 30 * 1000);
	["keydown", "pointerdown", "touchstart", "wheel"].forEach((ev) => window.addEventListener(ev, () => { ultimoUso = Date.now(); }, { passive: true, capture: true }));
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") {
			checkVersion();
		} else {
			recargarSiConviene();
		}
	});
	window.addEventListener("error", (event) => {
		const error = (event && (event.error || event.message)) || "";
		if (esErrorDeChunk(error)) {
			recargarPorChunk(error);
		}
	});
	window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
		const reason: any = event && event.reason;
		if (esErrorDeChunk(reason)) {
			recargarPorChunk(reason);
		}
	});
};
